import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Monitor, Smartphone, Tv, Store, HardDrive, RefreshCw, Check, Ban, Trash2,
  Pencil, ChevronDown, Copy, Loader2, ShieldCheck, Clock, Wifi, WifiOff, Lock, Unlock, Eye, EyeOff,
  UserCheck, XCircle, History, KeyRound, Plus,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { useToast } from '../components/ui/use-toast';
import { confirm } from '../components/ui/confirm-dialog';
import { useRestaurant } from '../context/RestaurantContext';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LanguageContext';
import deviceService from '../services/deviceService';
import { locationsService } from '../services/locationsService';

/* Settings -> Devices: the register of machines allowed to run RestoBOXX, approved here before they can do anything. */

const DEVICE_TYPES = [
  { value: 'POS', label: 'POS terminal', Icon: Monitor },
  { value: 'ANDROID', label: 'Android phone / tablet', Icon: Smartphone },
  { value: 'KDS', label: 'Kitchen display', Icon: Tv },
  { value: 'KIOSK', label: 'Self-order kiosk', Icon: Store },
  { value: 'OTHER', label: 'Other', Icon: HardDrive },
];

const FINGERPRINT_LABEL = {
  MOTHERBOARD: 'Motherboard UUID',
  IMEI: 'IMEI',
  ANDROID_ID: 'Android ID',
  SERIAL: 'Hardware serial',
  OTHER: 'Hardware id',
};

// mm:ss until `iso`, or 0:00 once it is behind us. Shown beside a live code,
// because "expires at 15:42" is a sum a person has to do while standing at a till.
const countdown = (iso, now) => {
  if (!iso) return '0:00';
  const left = Math.max(0, Math.floor((new Date(iso).getTime() - now) / 1000));
  const h = Math.floor(left / 3600);
  const m = Math.floor((left % 3600) / 60);
  const sec = left % 60;
  const two = (n) => String(n).padStart(2, '0');
  return h ? `${h}:${two(m)}:${two(sec)}` : `${m}:${two(sec)}`;
};

const TABS = [
  { key: 'PENDING', label: 'Awaiting approval' },
  { key: 'ACTIVE', label: 'Active' },
  { key: 'DENIED', label: 'Denied' },
  { key: 'REVOKED', label: 'De-registered' },
];

// How each audit event reads in the trail, and the colour it carries.
const AUDIT_META = {
  ENROLLED: { label: 'Registered itself', tone: 'text-blue-600' },
  RE_ENROLLED: { label: 'Updated its build', tone: 'text-muted-foreground' },
  APPROVED: { label: 'Approved', tone: 'text-emerald-600' },
  DENIED: { label: 'Denied', tone: 'text-red-600' },
  REVOKED: { label: 'De-registered', tone: 'text-red-600' },
  UPDATED: { label: 'Details changed', tone: 'text-muted-foreground' },
  DELETED: { label: 'Registration removed', tone: 'text-red-600' },
  PASSWORD_SET: { label: 'Settings password set', tone: 'text-amber-600' },
  PASSWORD_CLEARED: { label: 'Settings password cleared', tone: 'text-amber-600' },
  SETTINGS_UNLOCKED: { label: 'Settings unlocked', tone: 'text-emerald-600' },
  SETTINGS_FAILED: { label: 'Wrong Settings password', tone: 'text-amber-600' },
  SETTINGS_LOCKED: { label: 'Locked out of Settings', tone: 'text-red-600' },
};

// One readable line per event, built from the event-specific `details` payload.
const auditDetail = (e) => {
  const d = e.details || {};
  switch (e.event) {
    case 'ENROLLED':
    case 'RE_ENROLLED':
      return [d.hostname, d.platform, d.app_version && `v${d.app_version}`]
        .filter(Boolean).join(' · ');
    // Data only, no sentence: the code that was used and which machine used it.
    case 'PAIRED':
      return [d.pairing_code, d.hostname, d.app_version && `v${d.app_version}`]
        .filter(Boolean).join(' · ');
    case 'APPROVED':
      return d.from_status === 'REVOKED' || d.from_status === 'DENIED'
        ? `Re-approved (was ${d.from_status.toLowerCase()})` : '';
    case 'DENIED':
    case 'REVOKED':
      return d.reason ? `“${d.reason}”` : 'No reason given';
    case 'UPDATED':
      return Object.entries(d.changes || {})
        .map(([f, [from, to]]) => `${f}: ${from || '—'} → ${to || '—'}`).join(', ');
    case 'PASSWORD_SET':
      return d.replaced_existing ? 'Replaced the previous password' : 'First password set';
    default:
      return '';
  }
};

const typeMeta = (value) => DEVICE_TYPES.find((t) => t.value === value) || DEVICE_TYPES[4];

const relative = (iso) => {
  if (!iso) return 'never';
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} h ago`;
  return `${Math.floor(secs / 86400)} d ago`;
};

// Tone classes are spelled out, not built by template string, since Tailwind only ships classes it can see.
const Tile = ({ label, value, tone = 'text-foreground' }) => (
  <Card className="shadow-sm">
    <CardContent className="p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${tone}`}>{value}</div>
    </CardContent>
  </Card>
);

export default function DevicesManagement() {
  const { restaurantId } = useRestaurant();
  const { toast } = useToast();
  const { t } = useT();
  const { hasPermission, isPermissionGated } = useAuth();
  // Fails open like every other module gate: a tenant whose catalog lacks the code keeps working as before.
  const canManage = !isPermissionGated('devices.manage') || hasPermission('devices.manage');

  const [devices, setDevices] = useState([]);
  const [counts, setCounts] = useState({});
  // Whether the server could read the socket roster at all, so an outage doesn't brand every terminal offline.
  const [socketRoster, setSocketRoster] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [tab, setTab] = useState('PENDING');
  const [query, setQuery] = useState('');

  // Approve / rename dialog — one form, two intents.
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', deviceType: 'POS', locationId: '' });
  const [saving, setSaving] = useState(false);

  // Settings-password dialog: write-only, so a password can be replaced or cleared but never read back.
  const [pwFor, setPwFor] = useState(null);
  const [pw, setPw] = useState({ value: '', confirm: '', show: false });
  const [pwSaving, setPwSaving] = useState(false);

  // Audit trail viewer. `null` = closed; otherwise {device, entries|null}.
  const [auditFor, setAuditFor] = useState(null);

  /* Pair a device — the setup half. A machine cannot be asked which branch it
     stands in (nobody has signed in on it yet), so the BRANCH is chosen here
     and travels inside the code. `minted` holds the code just created: it is
     shown once, big, because it is read out loud to whoever is at the machine. */
  const [pairOpen, setPairOpen] = useState(false);
  const [pairForm, setPairForm] = useState({
    locationId: '', deviceType: '', name: '', minutes: 30,
  });
  const [minting, setMinting] = useState(false);
  const [minted, setMinted] = useState(null);
  const [codes, setCodes] = useState([]);
  const [codeCopied, setCodeCopied] = useState(false);
  // Ticks only while the dialog is open, so the countdowns move without the
  // page re-rendering every second for nothing.
  const [now, setNow] = useState(() => Date.now());
  // Shifts moved to their own page — /settings/devices/<id>/shifts.
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!restaurantId) return;
    try {
      const data = await deviceService.list(restaurantId);
      setDevices(data.results || []);
      setCounts(data.counts || {});
      setSocketRoster(!!data.socket_roster_available);
    } catch (e) {
      toast({
        title: 'Could not load devices',
        description: e?.response?.data?.error || e.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [restaurantId, toast]);

  useEffect(() => {
    load();
    // Short poll: this screen is watched while a new terminal starts up, so a request should appear on its own.
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!restaurantId) return;
    locationsService.list(restaurantId).then(setLocations).catch(() => setLocations([]));
  }, [restaurantId]);

  const loadCodes = useCallback(async () => {
    if (!restaurantId) return;
    try {
      setCodes(await deviceService.pairingCodes(restaurantId));
    } catch {
      setCodes([]);
    }
  }, [restaurantId]);

  useEffect(() => {
    if (!pairOpen) return undefined;
    loadCodes();
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [pairOpen, loadCodes]);

  // Default the branch as soon as there is one to default to: a single-branch
  // restaurant should never be asked which of its one branch this is.
  useEffect(() => {
    if (!pairForm.locationId && locations.length) {
      setPairForm((f) => ({ ...f, locationId: String(locations[0].id) }));
    }
  }, [locations, pairForm.locationId]);

  const mintCode = async () => {
    if (!pairForm.locationId) {
      toast({
        title: t('settings.devices.pair.needBranch'),
        description: t('settings.devices.pair.needBranchBody'),
        variant: 'destructive',
      });
      return;
    }
    setMinting(true);
    try {
      const row = await deviceService.createPairingCode(restaurantId, {
        locationId: pairForm.locationId,
        deviceType: pairForm.deviceType,
        name: pairForm.name.trim(),
        minutes: Number(pairForm.minutes) || 30,
      });
      setMinted(row);
      setCodeCopied(false);
      loadCodes();
    } catch (e) {
      toast({
        title: t('settings.devices.pair.mintFailed'),
        description: e?.response?.data?.error || e.message,
        variant: 'destructive',
      });
    } finally {
      setMinting(false);
    }
  };

  const cancelCode = async (row) => {
    const ok = await confirm({
      title: t('settings.devices.pair.cancelTitle'),
      description: t('settings.devices.pair.cancelBody', { code: row.display_code }),
      confirmText: t('settings.devices.pair.cancelConfirm'),
      tone: 'destructive',
    });
    if (!ok) return;
    try {
      await deviceService.cancelPairingCode(restaurantId, row.id);
      if (minted?.id === row.id) setMinted(null);
      loadCodes();
    } catch (e) {
      toast({
        title: t('settings.devices.pair.cancelFailed'),
        description: e?.response?.data?.error || e.message,
        variant: 'destructive',
      });
    }
  };

  const copyCode = async (row) => {
    try {
      await navigator.clipboard.writeText(row.display_code || row.code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 1600);
    } catch { /* the code is on screen in 32px type — copying is a convenience */ }
  };

  // Land on whichever bucket has something to do: requests first.
  useEffect(() => {
    if (!loading && counts.pending === 0 && tab === 'PENDING') setTab('ACTIVE');
    // Only on the first load — afterwards the tab is the user's choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return devices
      .filter((d) => d.status === tab)
      .filter((d) => !q || [d.name, d.fingerprint, d.hostname, d.model_name, d.location_name,
        // "Who asked for this?" is a question staff bring here, so the requester is searchable too.
        d.requested_by_name]
        .some((v) => (v || '').toLowerCase().includes(q)));
  }, [devices, tab, query]);

  const openApprove = (d) => {
    setForm({
      name: d.name || d.hostname || '',
      deviceType: d.device_type || 'POS',
      locationId: d.location_id ? String(d.location_id) : '',
    });
    setEditing({ device: d, mode: d.status === 'ACTIVE' ? 'edit' : 'approve' });
  };

  const saveDialog = async () => {
    if (!editing) return;
    const { device, mode } = editing;
    if (!form.name.trim()) {
      toast({ title: 'Name this device', description: 'Give it a label staff will recognise, e.g. "Counter 1 POS".', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        deviceType: form.deviceType,
        locationId: form.locationId ? Number(form.locationId) : null,
      };
      if (mode === 'approve') {
        await deviceService.approve(restaurantId, device.id, payload);
        toast({
          title: 'Device approved',
          description: `${payload.name} will connect within a few seconds.`,
        });
      } else {
        await deviceService.update(restaurantId, device.id, payload);
        toast({ title: 'Device updated', description: payload.name });
      }
      setEditing(null);
      load();
    } catch (e) {
      toast({
        title: mode === 'approve' ? 'Approval failed' : 'Update failed',
        description: e?.response?.data?.error || e.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const deny = async (d) => {
    const ok = await confirm({
      title: 'Deny this device?',
      description: `${d.name || d.hostname || 'This machine'} will be refused and cannot run `
        + 'RestoBOXX. The request is kept on record, so the same machine re-enrolling stays '
        + 'denied rather than reappearing as a fresh request. You can approve it later.',
      confirmText: 'Deny device',
      tone: 'destructive',
    });
    if (!ok) return;
    setBusyId(d.id);
    try {
      await deviceService.deny(restaurantId, d.id);
      toast({ title: 'Device denied', description: d.name || d.fingerprint });
      load();
    } catch (e) {
      toast({
        title: 'Could not deny',
        description: e?.response?.data?.error || e.message,
        variant: 'destructive',
      });
    } finally {
      setBusyId(null);
    }
  };

  const openAudit = async (d) => {
    setAuditFor({ device: d, entries: null });
    try {
      // By fingerprint, not id, so the trail follows the machine across registrations and deleted rows.
      const entries = await deviceService.audit(restaurantId, { fingerprint: d.fingerprint });
      setAuditFor({ device: d, entries });
    } catch (e) {
      setAuditFor({ device: d, entries: [], error: e?.response?.data?.error || e.message });
    }
  };

  const openPassword = (d) => {
    setPw({ value: '', confirm: '', show: false });
    setPwFor(d);
  };

  const savePassword = async () => {
    if (!pwFor) return;
    if (pw.value.length < 4) {
      toast({ title: 'Too short', description: 'Use at least 4 characters.', variant: 'destructive' });
      return;
    }
    if (pw.value !== pw.confirm) {
      toast({ title: 'They don’t match', description: 'Re-type the same password in both boxes.', variant: 'destructive' });
      return;
    }
    setPwSaving(true);
    try {
      await deviceService.setSettingsPassword(restaurantId, pwFor.id, pw.value);
      toast({
        title: 'Settings password set',
        description: `${pwFor.name || 'This terminal'} — staff at the till now need this password to open Settings.`,
      });
      setPwFor(null);
      load();
    } catch (e) {
      toast({ title: 'Could not set password', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    } finally {
      setPwSaving(false);
    }
  };

  const clearPassword = async () => {
    if (!pwFor) return;
    const ok = await confirm({
      title: 'Clear the Settings password?',
      description: `${pwFor.name || 'This terminal'} will no longer be able to open its Settings screen at all `
        + '— printers and the customer display can only be changed after you set a new password. '
        + 'This does not unlock it.',
      confirmText: 'Clear password',
      tone: 'destructive',
    });
    if (!ok) return;
    setPwSaving(true);
    try {
      await deviceService.clearSettingsPassword(restaurantId, pwFor.id);
      toast({ title: 'Settings password cleared', description: 'That terminal can no longer open Settings.' });
      setPwFor(null);
      load();
    } catch (e) {
      toast({ title: 'Could not clear password', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    } finally {
      setPwSaving(false);
    }
  };

  const revoke = async (d) => {
    const ok = await confirm({
      title: 'De-register this device?',
      description: `${d.name || d.fingerprint} loses its access token immediately and stops `
        + 'receiving live orders and notifications. It can be approved again later.',
      confirmText: 'De-register',
      tone: 'destructive',
    });
    if (!ok) return;
    setBusyId(d.id);
    try {
      await deviceService.revoke(restaurantId, d.id);
      toast({ title: 'Device de-registered', description: d.name || d.fingerprint });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (d) => {
    const ok = await confirm({
      title: 'Remove this device from the register?',
      description: 'The record and its history are deleted. The machine can enrol again from '
        + 'scratch and will come back as a new request.',
      confirmText: 'Remove',
      tone: 'destructive',
    });
    if (!ok) return;
    setBusyId(d.id);
    try {
      await deviceService.remove(restaurantId, d.id);
      toast({ title: 'Device removed' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.error || e.message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  /* "just now" answers "is this fresh?"; a manager signing off a terminal
     also needs the moment itself. Both, rather than a choice between them. */
  const stamp = (iso) => {
    if (!iso) return null;
    const when = new Date(iso);
    if (Number.isNaN(when.getTime())) return null;
    return `${when.toLocaleString([], {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })} · ${relative(iso)}`;
  };

  /* One row open at a time: this is a list to scan, and three expanded
     cards put the next terminal off the bottom of the screen. */
  const [openId, setOpenId] = useState(null);

  const copyFingerprint = (d) => {
    navigator.clipboard?.writeText(d.fingerprint);
    toast({ title: 'Hardware id copied', description: d.fingerprint });
  };

  const renderRow = (d) => {
    const { Icon, label } = typeMeta(d.device_type);
    const isOpen = openId === d.id;
    return (
      <div key={d.id} className="rounded-xl border border-border bg-card p-4 flex flex-wrap items-start gap-x-4 gap-y-3">
        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>

        <div className="min-w-[240px] flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            {/* The name is the way into the rest. Everything this device
                carries — its hardware id, who asked for it, who approved it
                and when — used to sit open on every row, so a register of
                twenty terminals was a wall of small print nobody read. It is
                one click away now, and the row keeps only what tells them
                apart: what it is called and whether it is working. */}
            <button
              type="button"
              onClick={() => setOpenId((cur) => (cur === d.id ? null : d.id))}
              aria-expanded={isOpen}
              className="group inline-flex items-center gap-1.5 font-semibold text-foreground
                         hover:text-primary focus:outline-none focus-visible:ring-2
                         focus-visible:ring-ring rounded"
              title={isOpen ? 'Hide the details' : 'Who asked for it, who approved it, and the machine itself'}
            >
              {d.name || d.hostname || 'Unnamed device'}
              <ChevronDown
                size={14}
                className={`text-muted-foreground transition-transform group-hover:text-primary
                            ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {d.status === 'ACTIVE' && (
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                d.is_online ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
              }`}>
                <span className={`h-1.5 w-1.5 rounded-full ${d.is_online ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                {d.is_online ? 'Live now' : `Last seen ${relative(d.last_seen_at)}`}
              </span>
            )}
            {/* A separate badge: "Live now" only means a recent HTTP call, while this one is the realtime link itself. */}
            {d.status === 'ACTIVE' && socketRoster && (
              <span
                className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                  d.socket_connected ? 'bg-sky-100 text-sky-700' : 'bg-amber-100 text-amber-800'
                }`}
                title={d.socket_connected
                  ? 'Holding a live WebSocket — orders and reservation requests reach this terminal instantly.'
                  : 'No WebSocket. The terminal still works over its regular polling, just without instant push.'}
              >
                {d.socket_connected ? <Wifi size={11} /> : <WifiOff size={11} />}
                {d.socket_connected ? 'WebSocket live' : 'No WebSocket'}
              </span>
            )}
            {d.status === 'PENDING' && (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                Awaiting approval
              </span>
            )}
            {d.status === 'REVOKED' && (
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                De-registered {d.revoked_at ? relative(d.revoked_at) : ''}
              </span>
            )}
          </div>

          <div className="text-xs text-muted-foreground mt-1">
            {label}
            {d.location_name ? ` · ${d.location_name}` : ' · no branch set'}
            {d.platform ? ` · ${d.platform}` : ''}
            {d.os_version ? ` ${d.os_version}` : ''}
            {d.app_version ? ` · app ${d.app_version}` : ''}
          </div>

          {/* One line that always shows: a PENDING request has to be judged
              from the list, and "somebody asked, nothing said who" is the
              thing a manager must not miss. */}
          {d.status === 'PENDING' && (
            <div className="text-[11px] mt-1 flex items-center gap-1.5">
              <UserCheck size={11} className="text-primary shrink-0" />
              {d.requested_by_name ? (
                <span className="text-muted-foreground">
                  Requested by{' '}
                  <span className="font-medium text-foreground">{d.requested_by_name}</span>
                  {d.requested_at ? ` · ${relative(d.requested_at)}` : ''}
                </span>
              ) : (
                <span className="text-muted-foreground italic">
                  Requested before RestoBOXX recorded who asks — check the machine
                  before approving it.
                </span>
              )}
            </div>
          )}

          {isOpen && (
            <div className="mt-3 border-t border-border/70 pt-3 grid gap-3 sm:grid-cols-2">
              {/* The machine itself. */}
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  This machine
                </div>
                <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] text-muted-foreground">
                    {FINGERPRINT_LABEL[d.fingerprint_kind] || 'Hardware id'}
                  </span>
                  <code className="text-[11px] font-mono bg-muted px-1.5 py-0.5 rounded break-all">
                    {d.fingerprint}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyFingerprint(d)}
                    title="Copy hardware id"
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Copy size={12} />
                  </button>
                </div>
                {(d.hostname || d.model_name || d.last_ip) && (
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {[d.hostname, d.model_name, d.last_ip].filter(Boolean).join(' · ')}
                  </div>
                )}
                {d.status === 'ACTIVE' && (
                  <div className="text-[11px] mt-1.5 flex items-center gap-1.5">
                    {d.has_settings_password ? (
                      <>
                        <Lock size={11} className="text-emerald-600" />
                        <span className="text-emerald-700">
                          Settings locked
                          {d.settings_password_set_by_name ? ` — set by ${d.settings_password_set_by_name}` : ''}
                        </span>
                        {d.settings_locked && (
                          <span className="text-amber-600">· temporarily locked out (too many tries)</span>
                        )}
                      </>
                    ) : (
                      <>
                        <Unlock size={11} className="text-amber-600" />
                        <span className="text-amber-700">
                          No Settings password — this terminal can’t open its Settings screen
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* How it got here: asked for, answered, and by whom — each with
                  the moment it happened, not only "2 days ago". */}
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  How it got here
                </div>
                <dl className="mt-1.5 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-[11px]">
                  <dt className="text-muted-foreground">Requested</dt>
                  <dd className="text-foreground">
                    {d.requested_by_name || d.requested_at ? (
                      <>
                        {d.requested_by_name
                          ? <span className="font-medium">{d.requested_by_name}</span>
                          : <span className="text-muted-foreground italic">not recorded</span>}
                        {stamp(d.requested_at) && (
                          <span className="block text-muted-foreground">{stamp(d.requested_at)}</span>
                        )}
                      </>
                    ) : <span className="text-muted-foreground">—</span>}
                  </dd>

                  <dt className="text-muted-foreground">Approved</dt>
                  <dd className="text-foreground">
                    {d.approved_by_name || d.approved_at ? (
                      <>
                        {d.approved_by_name
                          ? <span className="font-medium">{d.approved_by_name}</span>
                          : <span className="text-muted-foreground italic">not recorded</span>}
                        {stamp(d.approved_at) && (
                          <span className="block text-muted-foreground">{stamp(d.approved_at)}</span>
                        )}
                      </>
                    ) : <span className="text-muted-foreground">—</span>}
                  </dd>

                  {(d.status === 'REVOKED' || d.status === 'DENIED') && (
                    <>
                      <dt className="text-muted-foreground">
                        {d.status === 'DENIED' ? 'Denied' : 'De-registered'}
                      </dt>
                      <dd className="text-red-600">
                        {d.revoked_by_name
                          ? <span className="font-medium">{d.revoked_by_name}</span>
                          : <span className="italic">not recorded</span>}
                        {stamp(d.revoked_at) && (
                          <span className="block">{stamp(d.revoked_at)}</span>
                        )}
                        {d.revoke_reason && <span className="block">“{d.revoke_reason}”</span>}
                      </dd>
                    </>
                  )}

                  {d.status === 'ACTIVE' && (
                    <>
                      <dt className="text-muted-foreground">Last seen</dt>
                      <dd className="text-foreground">
                        {stamp(d.last_seen_at) || <span className="text-muted-foreground">—</span>}
                      </dd>
                    </>
                  )}
                </dl>
              </div>
            </div>
          )}
        </div>

        {canManage && (
          <div className="ml-auto flex items-center gap-2">
            {/* Available on every row in every state: the trail is why denied and de-registered records are kept. */}
            <Button size="sm" variant="outline" className="h-8 px-2.5 text-xs"
              disabled={busyId === d.id} onClick={() => openAudit(d)}
              title="History — who did what to this device, and when">
              <History size={13} />
            </Button>
            {/* Only where there are shifts to read, opening the full-page report of totals and daily ledger. */}
            {d.status === 'ACTIVE' && (
              <Button size="sm" variant="outline" className="h-8 px-2.5 text-xs"
                disabled={busyId === d.id}
                onClick={() => navigate(`/settings/devices/${d.id}/shifts`)}
                title="Shift report — who worked this terminal, and for how long">
                <Clock size={13} />
              </Button>
            )}
            {d.status !== 'ACTIVE' && (
              <Button
                size="sm"
                disabled={busyId === d.id}
                onClick={() => openApprove(d)}
                className="h-8 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Check size={13} className="mr-1" />
                {d.status === 'PENDING' ? 'Approve' : 'Re-approve'}
              </Button>
            )}
            {d.status === 'PENDING' && (
              <Button size="sm" variant="outline"
                className="h-8 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50"
                disabled={busyId === d.id} onClick={() => deny(d)}
                title="Refuse this request but keep it on record">
                <XCircle size={13} className="mr-1" /> Deny
              </Button>
            )}
            {d.status === 'ACTIVE' && (
              <>
                <Button size="sm" variant="outline" className="h-8 px-3 text-xs"
                  disabled={busyId === d.id} onClick={() => openPassword(d)}
                  title="Password that opens this terminal's own Settings screen">
                  <Lock size={13} className="mr-1" />
                  {d.has_settings_password ? 'Change password' : 'Set password'}
                </Button>
                <Button size="sm" variant="outline" className="h-8 px-3 text-xs"
                  disabled={busyId === d.id} onClick={() => openApprove(d)}>
                  <Pencil size={13} className="mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline"
                  className="h-8 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50"
                  disabled={busyId === d.id} onClick={() => revoke(d)}>
                  <Ban size={13} className="mr-1" /> De-register
                </Button>
              </>
            )}
            {d.status !== 'ACTIVE' && (
              <Button size="sm" variant="outline"
                className="h-8 px-3 text-xs text-red-600 border-red-200 hover:bg-red-50"
                disabled={busyId === d.id} onClick={() => remove(d)}>
                {busyId === d.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 w-full space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none flex items-center gap-2">
            <ShieldCheck className="h-7 w-7 text-primary" /> Registered Devices
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Setting a machine up starts HERE, not on the machine: it is the
              branch that has to be decided, and only this screen knows them. */}
          {canManage && (
            <Button onClick={() => { setMinted(null); setPairOpen(true); }}>
              <KeyRound size={15} className="mr-2" /> {t('settings.devices.pair.button')}
            </Button>
          )}
          <Button variant="outline" onClick={load} disabled={loading}>
            <RefreshCw size={15} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Tile label="Awaiting approval" value={counts.pending ?? 0} tone="text-amber-600" />
        <Tile label="Active" value={counts.active ?? 0} tone="text-emerald-600" />
        <Tile label="Live now" value={counts.online ?? 0} tone="text-sky-600" />
        <Tile label="Denied" value={counts.denied ?? 0} tone="text-red-600" />
        <Tile label="De-registered" value={counts.revoked ?? 0} tone="text-red-600" />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {TABS.map((t) => {
          const n = counts[t.key.toLowerCase()] ?? 0;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                tab === t.key
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-card text-muted-foreground border-border hover:border-foreground/40'
              }`}
            >
              {t.label}
              {n > 0 && <span className="ml-1.5 opacity-70">{n}</span>}
            </button>
          );
        })}
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, hardware id, branch…"
          className="h-8 w-64 ml-auto text-sm"
        />
      </div>

      {loading ? (
        <div className="py-16 text-center text-muted-foreground flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading devices…
        </div>
      ) : visible.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="p-10 text-center text-sm text-muted-foreground">
            {tab === 'PENDING'
              ? 'No devices are waiting for approval. Start the POS app or the Android app on a new '
                + 'machine and its request appears here.'
              : tab === 'ACTIVE'
                ? 'No approved devices yet.'
                : 'Nothing has been de-registered.'}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">{visible.map(renderRow)}</div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing?.mode === 'approve' ? 'Approve this device' : 'Edit device'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {editing && (
              <div className="text-xs text-muted-foreground">
                {FINGERPRINT_LABEL[editing.device.fingerprint_kind] || 'Hardware id'}:{' '}
                <code className="font-mono break-all">{editing.device.fingerprint}</code>
              </div>
            )}
            <div>
              <Label htmlFor="device-name">Name *</Label>
              <Input
                id="device-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Counter 1 POS"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="device-type">Type</Label>
              <select
                id="device-type"
                value={form.deviceType}
                onChange={(e) => setForm((f) => ({ ...f, deviceType: e.target.value }))}
                className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {DEVICE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="device-location">Branch</Label>
              <select
                id="device-location"
                value={form.locationId}
                onChange={(e) => setForm((f) => ({ ...f, locationId: e.target.value }))}
                className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">No branch</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button onClick={saveDialog} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editing?.mode === 'approve' ? 'Approve device' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings password — write-only; a forgotten one is replaced, not read. */}
      <Dialog open={!!pwFor} onOpenChange={(o) => !o && setPwFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock size={16} />
              {pwFor?.has_settings_password ? 'Change' : 'Set'} Settings password
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/40 p-3">
              <div className="text-sm font-semibold">{pwFor?.name || 'Unnamed device'}</div>
              <div className="text-[11px] font-mono text-muted-foreground break-all mt-1">
                {pwFor?.fingerprint}
              </div>
            </div>

            <div>
              <Label htmlFor="dev-pw">New password</Label>
              <div className="relative">
                <Input
                  id="dev-pw"
                  type={pw.show ? 'text' : 'password'}
                  value={pw.value}
                  autoComplete="new-password"
                  maxLength={64}
                  onChange={(e) => setPw((p) => ({ ...p, value: e.target.value }))}
                  placeholder="At least 4 characters"
                  className="pr-9"
                />
                <button
                  type="button"
                  onClick={() => setPw((p) => ({ ...p, show: !p.show }))}
                  aria-label={pw.show ? 'Hide password' : 'Show password'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {pw.show ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="dev-pw2">Repeat password</Label>
              <Input
                id="dev-pw2"
                type={pw.show ? 'text' : 'password'}
                value={pw.confirm}
                autoComplete="new-password"
                maxLength={64}
                onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
              />
              {pw.confirm && pw.value !== pw.confirm && (
                <p className="text-[11px] text-red-600 mt-1">These don’t match.</p>
              )}
            </div>

            {pwFor?.has_settings_password && (
              <p className="text-[11px] text-muted-foreground">
                Set by {pwFor.settings_password_set_by_name || 'a manager'}.
              </p>
            )}
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            {pwFor?.has_settings_password ? (
              <Button variant="outline" onClick={clearPassword} disabled={pwSaving}
                className="text-red-600 border-red-200 hover:bg-red-50">
                <Unlock size={14} className="mr-1.5" /> Clear
              </Button>
            ) : <span />}
            <span className="flex gap-2">
              <Button variant="outline" onClick={() => setPwFor(null)} disabled={pwSaving}>Cancel</Button>
              <Button onClick={savePassword} disabled={pwSaving}>
                {pwSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save password
              </Button>
            </span>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit trail — every action on this machine, newest first. */}
      <Dialog open={!!auditFor} onOpenChange={(o) => !o && setAuditFor(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History size={16} />
              History — {auditFor?.device?.name || auditFor?.device?.hostname || 'device'}
            </DialogTitle>
          </DialogHeader>

          <div className="text-[11px] font-mono text-muted-foreground break-all -mt-2 mb-1">
            {auditFor?.device?.fingerprint}
          </div>

          {auditFor?.entries === null ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading history…
            </div>
          ) : auditFor?.error ? (
            <div className="py-10 text-center text-sm text-red-600">{auditFor.error}</div>
          ) : (auditFor?.entries || []).length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Nothing recorded for this machine yet. Entries appear from the moment it
              first registers.
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1">
              {auditFor.entries.map((e, i) => {
                const meta = AUDIT_META[e.event] || { label: e.event_label, tone: 'text-foreground' };
                const detail = auditDetail(e);
                return (
                  <div key={e.id} className="flex gap-3 text-sm">
                    <div className="w-1.5 shrink-0 flex flex-col items-center pt-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full bg-current ${meta.tone}`} />
                      {i < auditFor.entries.length - 1 && (
                        <span className="flex-1 w-px bg-border mt-1" />
                      )}
                    </div>
                    <div className="pb-1 min-w-0 flex-1">
                      <div className={`font-medium ${meta.tone}`}>{meta.label}</div>
                      {detail && (
                        <div className="text-xs text-muted-foreground mt-0.5 break-words">{detail}</div>
                      )}
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {e.created_at ? new Date(e.created_at).toLocaleString() : ''}
                        {' · '}
                        {e.actor_name || 'System'}
                        {e.actor_type === 'device' ? ' (the terminal)' : ''}
                        {e.ip ? ` · ${e.ip}` : ''}
                      </div>
                      {e.device_removed && (
                        <div className="text-[11px] text-amber-600 mt-0.5">
                          Recorded against a registration that has since been removed
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditFor(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pair a device — mint a code, read it out, the machine sets itself up. */}
      <Dialog open={pairOpen} onOpenChange={(o) => !o && setPairOpen(false)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound size={16} /> {t('settings.devices.pair.title')}
            </DialogTitle>
          </DialogHeader>

          {minted ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('settings.devices.pair.mintedBlurb', {
                  branch: minted.location_name || '',
                })}
              </p>

              <div className="rounded-lg border-2 border-dashed border-primary/40 bg-primary/5 p-5 text-center">
                <div className="font-mono text-3xl font-bold tracking-[0.2em] text-foreground break-all">
                  {minted.display_code}
                </div>
                <div className="mt-2 flex items-center justify-center gap-3">
                  <Button variant="outline" size="sm" onClick={() => copyCode(minted)}>
                    {codeCopied ? <Check size={14} className="mr-1.5" /> : <Copy size={14} className="mr-1.5" />}
                    {codeCopied ? t('settings.devices.pair.copied') : t('settings.devices.pair.copy')}
                  </Button>
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                    <Clock size={12} />
                    {t('settings.devices.pair.expiresIn', {
                      time: countdown(minted.expires_at, now),
                    })}
                  </span>
                </div>
              </div>

              <ol className="space-y-1.5 text-sm text-muted-foreground list-decimal pl-5">
                <li>{t('settings.devices.pair.step1')}</li>
                <li>{t('settings.devices.pair.step2')}</li>
                <li>{t('settings.devices.pair.step3')}</li>
              </ol>
              <p className="text-xs text-muted-foreground">
                {t('settings.devices.pair.oneUse')}
              </p>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setMinted(null)}>
                  <Plus size={14} className="mr-1.5" /> {t('settings.devices.pair.another')}
                </Button>
                <Button onClick={() => setPairOpen(false)}>{t('settings.devices.pair.done')}</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{t('settings.devices.pair.blurb')}</p>

              <div>
                <Label htmlFor="pair-branch">{t('settings.devices.pair.branch')}</Label>
                <select
                  id="pair-branch"
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  value={pairForm.locationId}
                  onChange={(e) => setPairForm((f) => ({ ...f, locationId: e.target.value }))}
                >
                  {locations.length === 0 && <option value="">—</option>}
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {t('settings.devices.pair.branchHint')}
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="pair-type">{t('settings.devices.pair.type')}</Label>
                  <select
                    id="pair-type"
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={pairForm.deviceType}
                    onChange={(e) => setPairForm((f) => ({ ...f, deviceType: e.target.value }))}
                  >
                    <option value="">{t('settings.devices.pair.anyType')}</option>
                    {DEVICE_TYPES.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="pair-minutes">{t('settings.devices.pair.expiry')}</Label>
                  <select
                    id="pair-minutes"
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    value={pairForm.minutes}
                    onChange={(e) => setPairForm((f) => ({ ...f, minutes: e.target.value }))}
                  >
                    <option value={30}>{t('settings.devices.pair.mins30')}</option>
                    <option value={120}>{t('settings.devices.pair.hours2')}</option>
                    <option value={480}>{t('settings.devices.pair.hours8')}</option>
                    <option value={720}>{t('settings.devices.pair.hours12')}</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="pair-name">{t('settings.devices.pair.name')}</Label>
                <Input
                  id="pair-name"
                  value={pairForm.name}
                  maxLength={120}
                  placeholder={t('settings.devices.pair.namePlaceholder')}
                  onChange={(e) => setPairForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>

              {/* Codes still live. A code read out to the wrong person is a
                  machine waiting to walk in, so they can be taken back. */}
              {codes.filter((c) => c.state === 'ACTIVE').length > 0 && (
                <div className="rounded-lg border border-border divide-y">
                  {codes.filter((c) => c.state === 'ACTIVE').map((c) => (
                    <div key={c.id} className="flex items-center justify-between gap-3 p-2.5">
                      <div className="min-w-0">
                        <div className="font-mono text-sm font-semibold">{c.display_code}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {c.location_name}
                          {c.device_type ? ` · ${DEVICE_TYPES.find((d) => d.value === c.device_type)?.label || c.device_type}` : ''}
                          {` · ${t('settings.devices.pair.expiresIn', { time: countdown(c.expires_at, now) })}`}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => cancelCode(c)}>
                        <XCircle size={14} className="mr-1.5" /> {t('settings.devices.pair.cancel')}
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setPairOpen(false)}>
                  {t('settings.devices.pair.close')}
                </Button>
                <Button onClick={mintCode} disabled={minting || !pairForm.locationId}>
                  {minting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <KeyRound size={14} className="mr-1.5" />}
                  {t('settings.devices.pair.create')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
